import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import NavBar from '../components/NavBar/NavBar';
import Footer from '../components/Footer/Footer';
import DailyRewardsModal from '../components/DailyRewardsModal/DailyRewardsModal';
import ReviewSectionNew from '../components/ReviewSection/ReviewSectionNew';
import ChapterDisplay from '../components/ChapterDisplay/ChapterDisplay';
import ChampionDisplay from '../components/ChampionDisplay/ChampionDisplay';
import { useAuth, useUser } from '../hooks/useAuth';
import ApiService from '../services/ApiService';
import checkinService from '../services/checkinService';
import novelService, { NovelDetail, RelatedNovel } from '../services/novelService';
import readingService, { ReadingProgress } from '../services/readingService';
import reviewService, { ReviewStats } from '../services/reviewService';
import { API_BASE_URL } from '../config';
import { debugAuthStatus } from '../utils/authDebug';

const BookDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user: authUser } = useAuth();
  const { user: userData } = useUser();
  const [showMore, setShowMore] = useState(false);
  const [tab, setTab] = useState('About');
  const [showDailyModal, setShowDailyModal] = useState(false);
  const hasAppliedQueryTabRef = useRef(false);
  
  // 小说数据状态
  const [book, setBook] = useState<NovelDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 评论统计状态
  const [reviewStats, setReviewStats] = useState<ReviewStats | null>(null);
  
  // 阅读进度状态
  const [readingProgress, setReadingProgress] = useState<ReadingProgress | null>(null);
  
  // 相关小说推荐状态
  const [relatedNovels, setRelatedNovels] = useState<RelatedNovel[]>([]);
  
  // 第一章ID状态（用于 START READING 按钮）
  const [firstChapterId, setFirstChapterId] = useState<number | null>(null);
  
  // 使用认证Hook，无需手动管理用户状态
  const user = authUser || userData;

  // 使用认证Hook，无需手动监听localStorage变化

  // 获取小说详情和阅读进度
  useEffect(() => {
    const loadNovelDetail = async () => {
      if (!id) {
        setError('小说ID不存在');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const novelDetail = await novelService.getNovelDetail(parseInt(id));
        setBook(novelDetail);
        
        // 如果当前 tab 是 Champion 但该小说未批准 Champion 功能，切换到 About tab
        if (tab === 'Champion' && novelDetail.champion_status !== 'approved') {
          setTab('About');
        }
        
        // 获取评论统计
        try {
          const stats = await reviewService.getNovelReviewStats(parseInt(id));
          setReviewStats(stats);
        } catch (statsError) {
          console.log('获取评论统计失败:', statsError);
        }
        
        // 如果用户已登录，获取阅读进度
        if (user) {
          try {
            const progress = await readingService.getUserReadingProgress(user.id, parseInt(id));
            setReadingProgress(progress);
          } catch (err) {
            console.error('获取阅读进度失败:', err);
            // 阅读进度获取失败不影响主要功能
          }
        }
        
        // 获取第一章ID（用于 START READING 按钮）
        try {
          const chapters = await novelService.getNovelChapters(parseInt(id));
          if (chapters.length > 0) {
            // 按 chapter_number 排序，获取最小章节号的章节
            const sortedChapters = [...chapters].sort((a, b) => a.chapter_number - b.chapter_number);
            setFirstChapterId(sortedChapters[0].id);
          }
        } catch (e) {
          console.log('获取第一章失败:', e);
        }
      } catch (err) {
        console.error('加载小说详情失败:', err);
        setError('加载小说详情失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };

    loadNovelDetail();
  }, [id, user]);

  // 仅支持 ?tab=champion：首次加载时根据 query + book 状态自动激活 Champion Tab
  // - 不引入新状态管理
  // - 不影响用户后续手动点击 Tabs（只应用一次）
  // - 如果 Champion 未启用则回退 About
  useEffect(() => {
    if (hasAppliedQueryTabRef.current) return;
    if (!book) return;

    const tabParam = searchParams.get('tab');
    if (tabParam !== 'champion') {
      hasAppliedQueryTabRef.current = true;
      return;
    }

    if (book.champion_status === 'approved') {
      setTab('Champion');
    } else {
      setTab('About');
    }

    hasAppliedQueryTabRef.current = true;
  }, [searchParams, book]);

  // 获取相关小说推荐
  useEffect(() => {
    const loadRelatedNovels = async () => {
      if (!id) return;
      try {
        const novels = await novelService.getRelatedNovels(parseInt(id), { limit: 5 });
        setRelatedNovels(novels);
      } catch (error) {
        console.error('获取相关小说失败:', error);
      }
    };
    loadRelatedNovels();
  }, [id]);

  // 检查是否应该显示签到弹窗
  useEffect(() => {
    const checkShouldShowModal = async () => {
      try {
        const shouldShow = await checkinService.shouldShowCheckinModal();
        setShowDailyModal(shouldShow);
      } catch (error) {
        console.error('检查签到弹窗状态失败:', error);
        setShowDailyModal(false);
      }
    };

    checkShouldShowModal();
  }, []);

  // 处理开始阅读按钮点击
  const handleStartReading = () => {
    if (!id) return;
    
    let targetChapterId: number | null = null;
    
    if (user && readingProgress) {
      // 已登录且有阅读记录，跳转到上次阅读的章节
      targetChapterId = readingProgress.chapter_id;
    } else {
      // 未登录或无阅读记录，跳转到第一章
      targetChapterId = firstChapterId;
    }
    
    if (targetChapterId) {
      // 跳转到章节阅读页面
      navigate(`/novel/${id}/chapter/${targetChapterId}`);
    } else {
      // 如果没有第一章ID（极端情况），提示用户
      console.log('无法获取章节信息');
    }
  };

  // 获取开始阅读按钮的文本
  const getStartReadingButtonText = () => {
    if (!user) return 'START READING';
    if (!readingProgress) return 'START READING';
    if (readingProgress.is_first_read) return 'START READING';
    return 'CONTINUE READING';
  };

  // 处理图片URL，确保使用完整的URL
  const getImageUrl = (coverPath: string) => {
    if (!coverPath) return '/default-cover.jpg'; // 默认封面
    
    // 如果已经是完整URL，直接返回
    if (coverPath.startsWith('http://') || coverPath.startsWith('https://')) {
      return coverPath;
    }
    
    // 如果是相对路径，添加API基础URL
    if (coverPath.startsWith('/')) {
      return `${API_BASE_URL}${coverPath}`;
    }
    
    // 如果是其他情况，直接返回
    return coverPath;
  };

  // 展开/收起简介逻辑
  const shortDesc = book?.description && book.description.length > 150 ? book.description.slice(0, 150) + '...' : book?.description || '';
  const showToggle = book?.description && book.description.length > 150;

  // 加载状态
  if (loading) {
    return (
      <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', color: 'var(--text-primary)', fontFamily: 'inherit', padding: '0 0 40px 0' }}>
        <NavBar />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '16px' }}>📚</div>
            <div>加载小说详情中...</div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // 错误状态
  if (error || !book) {
    return (
      <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', color: 'var(--text-primary)', fontFamily: 'inherit', padding: '0 0 40px 0' }}>
        <NavBar />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '16px' }}>❌</div>
            <div style={{ marginBottom: '16px' }}>{error || '小说不存在'}</div>
            <button 
              onClick={() => navigate('/')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              返回首页
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', color: 'var(--text-primary)', fontFamily: 'inherit', padding: '0 0 40px 0' }}>
      <DailyRewardsModal open={showDailyModal} onClose={() => setShowDailyModal(false)} />
      <NavBar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 24px 0 24px', display: 'flex', gap: 40 }}>
        {/* Cover */}
        <div style={{ flex: '0 0 260px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img 
            src={getImageUrl(book.cover)} 
            alt={book.title} 
            style={{ width: 240, height: 340, borderRadius: 8, boxShadow: '0 4px 32px var(--shadow-color)', objectFit: 'cover', background: 'var(--bg-tertiary)' }}
            onError={(e) => {
              e.currentTarget.src = '/default-cover.jpg';
            }}
          />
        </div>
        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ background: '#fff', color: '#222', fontWeight: 600, fontSize: 15, borderRadius: 6, padding: '2px 12px', marginRight: 8 }}>{book.status}</span>
            <div style={{ fontWeight: 700, fontSize: 32, marginTop: 8 }}>{book.title}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
            <span style={{ fontSize: 18 }}>👍 {reviewStats ? reviewStats.recommendation_rate : book.rating}%</span>
            <span style={{ fontSize: 18 }}>💙 {reviewStats ? reviewStats.total_reviews : book.reviews} Reviews</span>
          </div>
          <div style={{ marginBottom: 4 }}>
            <span style={{ color: 'var(--text-secondary)' }}>Author: </span><span style={{ fontWeight: 600 }}>{book.author}</span>
          </div>
          {book.translator && (
            <div style={{ marginBottom: 4 }}>
              <span style={{ color: 'var(--text-secondary)' }}>Translator: </span><span style={{ fontWeight: 600 }}>{book.translator}</span>
            </div>
          )}
          <div style={{ color: 'var(--text-secondary)', fontSize: 16, marginBottom: 8, lineHeight: 1.7, maxWidth: 600 }}>
            {showMore ? (book.description || '暂无简介') : shortDesc}
            {showToggle && (
              <div>
                <button
                  onClick={() => setShowMore(v => !v)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2196f3',
                    fontWeight: 600,
                    fontSize: 16,
                    cursor: 'pointer',
                    padding: 0,
                    marginTop: 4,
                  }}
                >
                  {showMore ? 'Show less 5e' : 'Show more 5f'}
                </button>
              </div>
            )}
          </div>
          <div style={{ margin: '24px 0 0 0' }}>
            <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '8px 18px', color: '#6cf', fontWeight: 600, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
              <span>🕒 2 Free Chapters Every 23 Hrs</span>
              <span style={{ color: 'var(--text-primary)', background: 'var(--bg-tertiary)', borderRadius: 4, padding: '2px 8px', fontSize: 15 }}>23:00:00</span>
            </div>
            <button 
              onClick={handleStartReading}
              style={{ background: 'linear-gradient(90deg, #1976d2 0%, #21a1ff 100%)', color: '#fff', fontWeight: 700, fontSize: 20, border: 'none', borderRadius: 10, padding: '14px 48px', cursor: 'pointer', boxShadow: '0 2px 12px #1976d244' }}
            >
              {getStartReadingButtonText()}
            </button>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div style={{ maxWidth: 1100, margin: '32px auto 0 auto', padding: '0 24px', borderBottom: '2px solid var(--border-color)', display: 'flex', gap: 32, fontSize: 20, fontWeight: 600, position: 'relative' }}>
        {['About', 'Chapters', ...(book?.champion_status === 'approved' ? ['Champion'] : [])].map((t) => (
          <div
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '12px 0',
              borderBottom: tab === t ? '3px solid #2196f3' : '3px solid transparent',
              color: tab === t ? 'var(--text-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              position: 'relative',
              flex: 1,
              textAlign: 'center',
            }}
          >
            {/* Sponsor this story! 气泡 */}
            {t === 'Champion' && tab === 'Champion' && (
              <div style={{
                position: 'absolute',
                left: '50%',
                top: -54,
                transform: 'translateX(-50%)',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
                <div style={{
                  background: 'linear-gradient(90deg, #7b61ff 0%, #4fc3f7 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 22,
                  borderRadius: 16,
                  padding: '10px 32px',
                  marginBottom: 0,
                  boxShadow: '0 2px 8px #0002',
                  whiteSpace: 'nowrap',
                }}>
                  Sponsor this story!
                </div>
                <div style={{
                  width: 0,
                  height: 0,
                  borderLeft: '14px solid transparent',
                  borderRight: '14px solid transparent',
                  borderTop: '14px solid #7b61ff',
                  marginTop: -2,
                  filter: 'drop-shadow(0 2px 4px #0002)',
                }} />
              </div>
            )}
            {t}
          </div>
        ))}
      </div>
      {/* About Tab Content */}
      {tab === 'About' && (
        <div style={{ maxWidth: 1100, margin: '32px auto 0 auto', padding: '0 24px', display: 'flex', gap: 80, flexDirection: 'column' }}>
          <div style={{ display: 'flex', gap: 80, alignItems: 'flex-end', marginBottom: 0, paddingBottom: 0 }}>
            <div>
              <div style={{ color: '#aaa', fontSize: 17, marginBottom: 8 }}>Chapters</div>
              <div style={{ fontWeight: 700, fontSize: 22 }}>{book.chapters} Chapters</div>
            </div>
            {book.licensed_from && (
              <div>
                <div style={{ color: '#aaa', fontSize: 17, marginBottom: 8 }}>Licensed From</div>
                <div style={{ fontWeight: 700, fontSize: 20 }}>{book.licensed_from}</div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0 }}>
            {/* 暂时使用默认标签，后续可以从数据库获取 */}
            {['Chinese', 'Comedy', 'Cultivation', 'Reincarnator', 'Xianxia', 'Action', 'Modern Setting'].map((tag) => (
              <span key={tag} style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', borderRadius: 8, padding: '6px 18px', fontWeight: 600, fontSize: 15 }}>{tag}</span>
            ))}
          </div>
          {/* 简介介绍区块 */}
          <div style={{ borderTop: '1px solid var(--border-color)', marginTop: 0, paddingTop: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 12 }}>Details</div>
            {book.translator && (
              <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: 16 }}>
                Translated by {book.translator}. The translator tag user name is {book.translator}.
              </div>
            )}
            <div style={{ fontWeight: 600, marginBottom: 12 }}>
              <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>Official Blurb.</span>
            </div>
            <div style={{ color: 'var(--text-primary)', fontSize: 17, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
              {book.description || '暂无详细描述'}
            </div>
          </div>
        {/* 评论区块 */}
        <ReviewSectionNew novelId={parseInt(id!)} user={user} />
        </div>
      )}
      {/* Chapters Tab Content */}
      {tab === 'Chapters' && (
        <ChapterDisplay novelId={parseInt(id!)} user={user} />
      )}
      
      {/* Related Novels Section */}
      {tab === 'About' && relatedNovels.length > 0 && (
        <div style={{ marginTop: 48 }}>
            <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 28, marginBottom: 24 }}>Related Novels</div>
            <div style={{ display: 'flex', gap: 32, justifyContent: 'center', flexWrap: 'wrap' }}>
              {relatedNovels.map((novel) => (
                <div 
                  key={novel.id} 
                  style={{ width: 160, background: 'var(--bg-secondary)', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px var(--shadow-color)', marginRight: 8, cursor: 'pointer' }}
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate(`/book/${novel.id}`);
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <img 
                      src={novel.cover ? getImageUrl(novel.cover) : '/default-cover.jpg'} 
                      alt={novel.title} 
                      style={{ width: '100%', height: 220, objectFit: 'cover' }} 
                    />
                    {novel.status && (
                      <span style={{ position: 'absolute', top: 8, left: 8, background: 'var(--bg-tertiary)', color: 'var(--text-primary)', borderRadius: 6, padding: '2px 10px', fontSize: 13, fontWeight: 600 }}>{novel.status}</span>
                    )}
                  </div>
                  <div style={{ padding: '12px 10px 8px 10px' }}>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 16, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{novel.title}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: 15, marginBottom: 4 }}>👍 {novel.rating ?? 0}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
      )}
      {/* Champion Tab Content */}
      {tab === 'Champion' && book?.champion_status === 'approved' && (
        <div style={{ maxWidth: 1100, margin: '32px auto 0 auto', padding: '0 24px' }}>
          <ChampionDisplay 
            novelId={book?.id || 0} 
            novelTitle={book?.title || ''}
            onSubscribe={(tierLevel) => {
              console.log('用户订阅了Champion等级:', tierLevel);
              // 可以在这里添加订阅成功后的逻辑
            }}
          />
        </div>
      )}
      <Footer />
    </div>
  );
};

export default BookDetail; 